import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { 
  FiGift, 
  FiCheck, 
  FiStar,
  FiAward,
  FiRefreshCw,
  FiExternalLink
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionService, { type UserSubscription, type SubscriptionTier } from '../services/subscriptionService';
import IconWrapper from '../components/IconWrapper';
import { auth } from '../lib/firebase';

const SubscriptionManagement: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [availableTiers, setAvailableTiers] = useState<SubscriptionTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [crowdfundingCode, setCrowdfundingCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');
  const currentTierCardBg = useColorModeValue('purple.50', 'purple.900');

  useEffect(() => {
    if (user) {
      loadSubscriptionData();
    }
  }, [user]);

  const loadSubscriptionData = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const [userSubscription, tiers] = await Promise.all([
        SubscriptionService.getUserSubscription(user.uid),
        SubscriptionService.getAvailableTiers()
      ]);
      
      setSubscription(userSubscription);
      setAvailableTiers(tiers);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      toast({
        title: 'Error loading subscription data',
        description: 'Failed to load your subscription information.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedeemCode = async () => {
    if (!user || !crowdfundingCode.trim()) return;
    
    setIsRedeeming(true);
    try {
      const newSubscription = await SubscriptionService.redeemCrowdfundingCode(
        crowdfundingCode.trim(),
        user.uid
      );
      
      if (newSubscription) {
        setSubscription(newSubscription);
        setCrowdfundingCode('');
        
        toast({
          title: 'Code redeemed successfully!',
          description: `You now have ${newSubscription.tier.name} benefits.`,
          status: 'success',
          duration: 5000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Invalid code',
          description: 'The crowdfunding code you entered is invalid or expired.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error redeeming code',
        description: error.message || 'Invalid or expired code',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setIsOpeningPortal(true);
      await SubscriptionService.openBillingPortal();
    } catch (error: any) {
      toast({
        title: 'Unable to open billing portal',
        description: error?.message || 'An error occurred while opening the billing portal.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleSubscribe = async (tierId: string) => {
    if (!user) {
      toast({
        title: 'Please sign in',
        description: 'You must be signed in to subscribe.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubscribing(tierId);
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be signed in to subscribe.');
      }

      const STRIPE_PAYMENT_LINKS: Record<string, string | undefined> = {
        premiumMonthly: process.env.REACT_APP_STRIPE_LINK_PREMIUM_MONTHLY || 'https://buy.stripe.com/test_eVqfZhfUs04sbRQa0q7EQ08',
        premiumYearly: process.env.REACT_APP_STRIPE_LINK_PREMIUM_YEARLY || 'https://buy.stripe.com/test_3cI7sLcIg8AY098egG7EQ07',
      };
      const STRIPE_PRICE_IDS: Record<string, string | undefined> = {
        premiumMonthly: process.env.REACT_APP_STRIPE_PRICE_PREMIUM_MONTHLY,
        premiumYearly: process.env.REACT_APP_STRIPE_PRICE_PREMIUM_YEARLY,
      };

      const paymentLink = STRIPE_PAYMENT_LINKS[tierId];
      const priceId = STRIPE_PRICE_IDS[tierId];
      if (!paymentLink || !priceId) {
        throw new Error('This subscription tier is not currently available.');
      }

      const url = new URL(paymentLink);
      if (currentUser.email) {
        url.searchParams.set('prefilled_email', currentUser.email);
      }
      url.searchParams.set('client_reference_id', currentUser.uid);
      url.searchParams.set('price_id', priceId);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (error: any) {
      toast({
        title: 'Unable to start checkout',
        description: error?.message || 'An error occurred while starting the checkout process.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubscribing(null);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze': return 'orange';
      case 'silver': return 'gray';
      case 'gold': return 'yellow';
      case 'platinum': return 'purple';
      default: return 'blue';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'bronze': return FiStar;
      case 'silver': return FiStar;
      case 'gold': return FiAward;
      case 'platinum': return FiGift;
      default: return FiStar;
    }
  };

  if (!user) {
    return (
      <Container maxW="1200px" py={8}>
        <Center py={12}>
          <Alert status="info" maxW="md">
            <AlertIcon />
            <AlertDescription>
              Please sign in to manage your subscription.
            </AlertDescription>
          </Alert>
        </Center>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxW="1200px" py={8}>
        <Center minH="50vh">
          <Spinner size="xl" color="purple.400" />
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="1200px" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <HStack justify="space-between" wrap="wrap" gap={4}>
          <VStack align="start" gap={2}>
            <HStack>
              <IconWrapper icon={FiAward} size={28} color="purple.400" />
              <Heading size="lg" color={textColor}>Premium Subscriptions</Heading>
            </HStack>
            <Text color={descriptionColor}>
              Manage your subscription, view available plans, and redeem crowdfunding codes
            </Text>
          </VStack>
          
          <Button
            leftIcon={<IconWrapper icon={FiRefreshCw} size={16} />}
            onClick={loadSubscriptionData}
            colorScheme="purple"
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        {/* Current Subscription Status */}
        {subscription ? (
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor} position="relative">
            <HStack justify="space-between" mb={4}>
              <HStack>
                <IconWrapper 
                  icon={getTierIcon(subscription.tier.id)} 
                  size={24} 
                  color={getTierColor(subscription.tier.id) + '.400'} 
                />
                <VStack align="start" gap={0}>
                  <Text fontSize="xl" fontWeight="bold" color={textColor}>
                    {subscription.tier.name}
                  </Text>
                  <Text fontSize="sm" color={descriptionColor}>
                    Active since {subscription.startDate.toLocaleDateString()}
                  </Text>
                </VStack>
              </HStack>
              
              <VStack align="end" gap={1}>
                <Badge 
                  colorScheme={getTierColor(subscription.tier.id)} 
                  size="lg"
                  position="absolute"
                  top={4}
                  right={4}
                >
                  {subscription.status.toUpperCase()}
                </Badge>
                <Text fontSize="sm" color={descriptionColor} mt={8}>
                  Expires {subscription.endDate.toLocaleDateString()}
                </Text>
              </VStack>
            </HStack>
            
            <VStack align="stretch" gap={3} mb={4}>
              <Text fontWeight="bold" color={textColor}>Benefits</Text>
              {subscription.tier.benefits.map((benefit, index) => (
                <HStack key={index}>
                  <IconWrapper icon={FiCheck} size={16} color="green.400" />
                  <Text fontSize="sm" color={textColor}>{benefit}</Text>
                </HStack>
              ))}
            </VStack>
            
          </Box>
        ) : (
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <Alert status="info" borderRadius="md" mb={4}>
              <AlertIcon />
              <AlertDescription>
                You don't have an active subscription. Subscribe to unlock premium features.
              </AlertDescription>
            </Alert>
          </Box>
        )}

        {/* Tabs */}
        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple">
          <TabList>
            <Tab>Subscription Tiers</Tab>
            <Tab>Redeem Code</Tab>
          </TabList>

          <TabPanels>
            {/* Subscription Tiers Tab */}
            <TabPanel px={0}>
              <VStack gap={6} align="stretch">
                <VStack align="start" gap={1}>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    Available Tiers
                  </Text>
                  <Text fontSize="sm" color={descriptionColor}>
                    View available subscription plans. Upgrade or manage your subscription through the billing portal.
                  </Text>
                </VStack>


                <VStack gap={4} align="stretch">
                  {availableTiers
                    .filter(tier => tier.id !== 'free' && tier.paymentLink)
                    .map(tier => {
                      const isCurrentTier = subscription?.tier.id === tier.id;
                      const isOtherPlan = subscription && !isCurrentTier;
                      const hasSubscription = Boolean(subscription);
                      
                      return (
                        <Box
                          key={tier.id}
                          border="1px"
                          borderColor={isCurrentTier ? 'purple.300' : borderColor}
                          borderRadius="lg"
                          p={6}
                          bg={isCurrentTier ? currentTierCardBg : cardBg}
                          transition="all 0.2s"
                          position="relative"
                        >
                          <VStack align="start" gap={3}>
                            <HStack w="100%" justify="space-between" position="relative">
                              <HStack>
                                <IconWrapper icon={getTierIcon(tier.id)} size={20} color={`${tier.color}.400`} />
                                <Text fontWeight="bold" fontSize="md" color={textColor}>{tier.name}</Text>
                                <Badge colorScheme={tier.color} fontSize="xs">
                                  ${tier.price.toFixed(2)}/{tier.id === 'premiumYearly' ? 'yr' : 'mo'}
                                </Badge>
                              </HStack>
                              {isCurrentTier && (
                                <Badge 
                                  colorScheme="green" 
                                  fontSize="sm" 
                                  px={2} 
                                  py={1}
                                  position="absolute"
                                  top={0}
                                  right={0}
                                >
                                  Current Plan
                                </Badge>
                              )}
                            </HStack>
                            <VStack align="start" gap={1.5} w="100%">
                              {tier.benefits.map((benefit, idx) => (
                                <HStack key={idx} gap={2}>
                                  <IconWrapper icon={FiCheck} size={14} color="green.400" />
                                  <Text fontSize="sm" color={descriptionColor}>{benefit}</Text>
                                </HStack>
                              ))}
                            </VStack>
                            <HStack w="100%" justify="flex-end" pt={2}>
                              {!hasSubscription && (
                                <Button
                                  colorScheme="purple"
                                  onClick={() => handleSubscribe(tier.id)}
                                  isLoading={isSubscribing === tier.id}
                                  loadingText="Redirecting..."
                                  rightIcon={<IconWrapper icon={FiExternalLink} size={16} />}
                                  size="md"
                                >
                                  Subscribe
                                </Button>
                              )}
                              {isOtherPlan && (
                                <Button
                                  colorScheme="purple"
                                  variant="outline"
                                  onClick={handleManageBilling}
                                  isLoading={isOpeningPortal}
                                  loadingText="Opening..."
                                  rightIcon={<IconWrapper icon={FiExternalLink} size={16} />}
                                  size="md"
                                >
                                  Manage Billing
                                </Button>
                              )}
                            </HStack>
                          </VStack>
                        </Box>
                      );
                    })}

                  {availableTiers.filter(tier => tier.id !== 'free' && tier.paymentLink).length === 0 && (
                    <Alert status="info">
                      <AlertIcon />
                      <AlertDescription>
                        Premium tiers are not yet configured. Please check back soon.
                      </AlertDescription>
                    </Alert>
                  )}
                </VStack>
              </VStack>
            </TabPanel>

            {/* Redeem Code Tab */}
            <TabPanel px={0}>
              <VStack gap={6} align="stretch">
                <VStack align="start" gap={1}>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    Redeem Crowdfunding Code
                  </Text>
                  <Text fontSize="sm" color={descriptionColor}>
                    Enter your crowdfunding reward code to unlock premium benefits.
                  </Text>
                </VStack>
                
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    Enter your crowdfunding reward code to unlock premium benefits.
                  </AlertDescription>
                </Alert>
                
                <FormControl>
                  <FormLabel>Reward Code</FormLabel>
                  <Input
                    value={crowdfundingCode}
                    onChange={(e) => setCrowdfundingCode(e.target.value.toUpperCase())}
                    placeholder="Enter your crowdfunding code"
                    bg={cardBg}
                    borderColor={borderColor}
                    color={textColor}
                    size="lg"
                  />
                </FormControl>
                
                <Button
                  colorScheme="purple"
                  onClick={handleRedeemCode}
                  isLoading={isRedeeming}
                  loadingText="Redeeming..."
                  isDisabled={!crowdfundingCode.trim()}
                  size="lg"
                  leftIcon={<IconWrapper icon={FiGift} size={18} />}
                >
                  Redeem Code
                </Button>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
};

export default SubscriptionManagement;
